import { z } from "zod";
import { WORKSPACE_ROLES_LABELS } from "@common/constants";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import {
    ASSIGNABLE_WORKSPACE_ROLES,
    UserWorkspaceRole,
} from "@common/userWorkspaceRoles";
import { EntityProfileType, EntityProfileIndustry } from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import { WorkspaceTimezone } from "@common/workspaceTimezone";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import {
    AssignableWorkspaceRole,
    workspaceLevelRoleDescription,
} from "@components/memberManagement/lib";
import {
    HistoricalDataValues,
    historicalDataSchema,
} from "@components/accountingMigration/historicalData";

const ADD_CLIENT_INVITE_ROLES: readonly UserWorkspaceRole[] = [
    UserWorkspaceRole.ADMIN,
    UserWorkspaceRole.MEMBER,
    UserWorkspaceRole.VIEW_ONLY,
];

/**
 * Derived from `ASSIGNABLE_WORKSPACE_ROLES` rather than listed here, so dropping
 * a role from that list stays a real kill switch: it closes the invite API and
 * this picker together instead of leaving an option on screen that the API
 * refuses. The intersection also fixes display order — Accountant is assignable
 * but deliberately not offered while setting a client up.
 */
export const addClientInviteRoleOptions = ASSIGNABLE_WORKSPACE_ROLES.filter(
    (role): role is AssignableWorkspaceRole =>
        ADD_CLIENT_INVITE_ROLES.includes(role),
).map((role) => ({
    value: role,
    label: WORKSPACE_ROLES_LABELS[role],
    description: workspaceLevelRoleDescription[role],
}));

export type AddClientMode = "newClient" | "addEntity";

export type CoaSetupFormMethod = "kick" | "template" | "entity";

const clientStepBaseSchema = z.object({
    clientName: z.string(),
    businessName: z.string().min(1, "Enter a business name"),
    bookkeepingStartDate: z.string().min(1, "Select a start date"),
    legalType: z.nativeEnum(EntityProfileType),
    servicesOffered: z
        .array(z.nativeEnum(OrganizationServicesOffered))
        .min(1, "Select at least one practice"),
    industries: z.array(z.nativeEnum(EntityProfileIndustry)).optional(),
    industryOther: z.string().optional(),
    timezone: z.nativeEnum(WorkspaceTimezone, {
        required_error: "Select a timezone",
    }),
});

export const clientStepSchema = clientStepBaseSchema.superRefine(
    (data, ctx) => {
        if (!data.clientName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Enter a client name",
                path: ["clientName"],
            });
        }
    },
);

export const addEntityClientStepSchema = clientStepBaseSchema;

export const planStepSchema = z.object({
    billingOwnership: z.boolean(),
    plan: z.nativeEnum(FLAT_RATE_PLANS),
});

export const inviteStepSchema = z.object({
    emails: z
        .array(z.string().email("Enter a valid email address"))
        .min(1, "Enter at least one email address"),
    role: z.nativeEnum(UserWorkspaceRole),
});

export const transferBillingStepSchema = z.object({
    email: z
        .string()
        .min(1, "Enter an email address")
        .email("Enter a valid email address"),
});

export enum AddClientSteps {
    CLIENT = "client",
    PLAN = "plan",
    INVITE = "invite",
    BILLING_TRANSFER = "billingTransfer",
}

export type StepSchema =
    | typeof clientStepSchema
    | typeof planStepSchema
    | typeof inviteStepSchema
    | typeof transferBillingStepSchema;

export const addClientFormSchema = z
    .object({
        mode: z.enum(["newClient", "addEntity"]),
        multiEntityFlow: z.boolean(),
        isMultiEntity: z.boolean().nullable(),
        client: clientStepBaseSchema,
        isAccrualLedgerEnabled: z.boolean(),
        migrationMethod: z
            .nativeEnum(AccountingMigrationExternalSystem)
            .nullable(),
        syncChartOfAccounts: z.boolean(),
        shouldUseExternalChartOfAccounts: z.boolean(),
        coaSetupMethod: z.enum(["kick", "template", "entity"]),
        coaTemplateId: z.string().uuid().nullable(),
        coaSourceEntityId: z.number().int().nullable(),
        historicalData: historicalDataSchema,
        plan: planStepSchema,
        invite: z.object({}).passthrough(),
        billingTransfer: z.object({}).passthrough(),
    })
    .superRefine((data, ctx) => {
        if (data.coaSetupMethod === "template" && data.coaTemplateId === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Select a template",
                path: ["coaTemplateId"],
            });
        }

        if (
            data.coaSetupMethod === "entity" &&
            data.coaSourceEntityId === null
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Select an entity",
                path: ["coaSourceEntityId"],
            });
        }

        if (data.mode === "newClient" && !data.client.clientName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Enter a client name",
                path: ["client", "clientName"],
            });
        }

        if (data.mode === "addEntity") {
            return;
        }

        if (data.isMultiEntity === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Select yes or no",
                path: ["isMultiEntity"],
            });
        }

        if (data.plan.billingOwnership) {
            if (data.multiEntityFlow) {
                return;
            }

            const result = inviteStepSchema.safeParse(data.invite);
            if (!result.success) {
                result.error.errors.forEach((err) => {
                    ctx.addIssue({ ...err, path: ["invite", ...err.path] });
                });
            }
        } else {
            const result = transferBillingStepSchema.safeParse(
                data.billingTransfer,
            );
            if (!result.success) {
                result.error.errors.forEach((err) => {
                    ctx.addIssue({
                        ...err,
                        path: ["billingTransfer", ...err.path],
                    });
                });
            }
        }
    });

export interface ClientFormSchema {
    mode: AddClientMode;
    client: z.infer<typeof clientStepBaseSchema>;
    multiEntityFlow: boolean;
    isMultiEntity: boolean | null;
    migrationMethod: AccountingMigrationExternalSystem | null;
    isAccrualLedgerEnabled: boolean;
    syncChartOfAccounts: boolean;
    shouldUseExternalChartOfAccounts: boolean;
    coaSetupMethod: CoaSetupFormMethod;
    coaTemplateId: string | null;
    coaSourceEntityId: number | null;
    historicalData: HistoricalDataValues;
    plan: z.infer<typeof planStepSchema>;
    invite: z.infer<typeof inviteStepSchema>;
    billingTransfer: z.infer<typeof transferBillingStepSchema>;
}
